'use client'

import ConfirmModal from '@/components/confirm-modal'
import { UserSelector } from '@/components/UserSelector'
import {
    CashRegisterDay,
    CashRegisterDirection,
    CashRegisterEntry,
    CashRegisterEntryBilling,
    CashRegisterScope,
    createCashRegisterEntry,
    deleteCashRegisterEntry,
    getCashRegisterCurrentDate,
    getCashRegisterDay,
    getCashRegisterEntryBillings,
    updateCashRegisterEntry,
} from '@/lib/api/financial/cash-register-api'
import { getCashRegisterPolicy } from '@/lib/api/financial/financial-settings-api'
import { getAccessibleCompanies } from '@/lib/api/organization/organization-api'
import {
    PAYMENT_METHOD_LABELS,
    PAYMENT_METHOD_ORDER,
} from '@/lib/billing/payment-labels'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import CurrencyInput from '@/pages/admin/financial/payment-account/components/currency-input'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { setAccessibleCompanies } from '@/store/slices/authSlice'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdBusiness,
    MdChevronLeft,
    MdChevronRight,
    MdDelete,
    MdEdit,
    MdExpandLess,
    MdExpandMore,
    MdHub,
    MdLocalAtm,
    MdOpenInNew,
    MdSave,
    MdToday,
} from 'react-icons/md'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const METHOD_OPTIONS = PAYMENT_METHOD_ORDER.map(value => ({
    value,
    label: PAYMENT_METHOD_LABELS[value] ?? value,
}))

function formatCurrency(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

/** `YYYY-MM-DD` por extenso, sem passar por fuso (é um dia, não um instante). */
function formatCashDate(day: string): string {
    const [year, month, date] = day.split('-').map(Number)
    return new Date(Date.UTC(year, month - 1, date)).toLocaleDateString(
        'pt-BR',
        {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            timeZone: 'UTC',
        }
    )
}

function addDays(day: string, amount: number): string {
    const date = new Date(`${day}T12:00:00.000Z`)
    date.setUTCDate(date.getUTCDate() + amount)
    return date.toISOString().slice(0, 10)
}

function formatTime(value: string | null): string {
    if (!value) return '-'
    return new Date(value).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    })
}

interface EntryForm {
    direction: CashRegisterDirection
    paymentMethod: string
    amount: number | null
    cashDate: string
    sellerId: number | null
    description: string
}

/**
 * Registro de caixa diário.
 *
 * O caixa do dia é a soma das linhas: as automáticas (uma por forma de pagamento,
 * somando as cobranças pagas e os estornos) e os lançamentos manuais. Um dia por
 * vez, de propósito — período é assunto de relatórios e lançamentos.
 */
export default function CashRegisterPage() {
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const accessibleCompanies = useAppSelector(s => s.auth.accessibleCompanies)
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'financial',
        'cash_register'
    )

    // Filtros na URL: voltar da cobrança aberta pelo detalhe devolve o mesmo dia.
    const [searchParams, setSearchParams] = useSearchParams()
    const dateParam = searchParams.get('date') || ''
    const methodFilter = searchParams.get('method') || ''
    const sellerParam = Number(searchParams.get('seller'))
    const sellerFilter =
        Number.isInteger(sellerParam) && sellerParam > 0 ? sellerParam : null
    const scope: CashRegisterScope =
        searchParams.get('scope') === 'organization' ? 'organization' : 'company'

    const [day, setDay] = useState<CashRegisterDay | null>(null)
    const [loading, setLoading] = useState(true)
    const [currentDate, setCurrentDate] = useState<string | null>(null)
    const [closingTime, setClosingTime] = useState<string | null>(null)

    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [billingsByEntry, setBillingsByEntry] = useState<
        Record<number, CashRegisterEntryBilling[]>
    >({})
    const [loadingBillingsId, setLoadingBillingsId] = useState<number | null>(
        null
    )

    const modalRef = useRef<HTMLDialogElement>(null)
    const [editing, setEditing] = useState<CashRegisterEntry | null>(null)
    const [form, setForm] = useState<EntryForm | null>(null)
    const [saving, setSaving] = useState(false)

    const [deleting, setDeleting] = useState<CashRegisterEntry | null>(null)
    const [deletingLoading, setDeletingLoading] = useState(false)

    const updateParams = useCallback(
        (changes: Record<string, string | null>) => {
            const next = new URLSearchParams(searchParams)
            for (const [key, value] of Object.entries(changes)) {
                if (value) next.set(key, value)
                else next.delete(key)
            }
            setSearchParams(next, { replace: true })
        },
        [searchParams, setSearchParams]
    )

    useEffect(() => {
        if (accessibleCompanies.length === 0) {
            getAccessibleCompanies()
                .then(res => {
                    if (res.success && res.result) {
                        dispatch(setAccessibleCompanies(res.result))
                    }
                })
                .catch(() => {})
        }
    }, [accessibleCompanies.length, dispatch])

    useEffect(() => {
        getCashRegisterPolicy()
            .then(res => {
                if (res.success) setClosingTime(res.result.closingTime)
            })
            .catch(() => {})
        // Aberta com data na URL, a tela ainda precisa saber qual é o caixa atual
        // (o "Caixa atual" e o selo de caixa aberto).
        getCashRegisterCurrentDate()
            .then(res => {
                if (res.success) setCurrentDate(res.result.date)
            })
            .catch(() => {})
    }, [user?.companyId])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const res = await getCashRegisterDay({
                date: dateParam || undefined,
                paymentMethod: methodFilter || undefined,
                sellerId: sellerFilter,
                scope,
            })
            if (res.success) {
                setDay(res.result)
                // Sem data na URL o backend devolve o caixa aberto agora: é ele
                // que vira o "Hoje" do botão, já com o horário de fechamento.
                if (!dateParam) setCurrentDate(res.result.date)
            } else {
                toast.error(res.error || 'Erro ao carregar o caixa')
            }
        } catch {
            toast.error('Erro ao carregar o caixa')
        } finally {
            setLoading(false)
        }
    }, [dateParam, methodFilter, sellerFilter, scope])

    useEffect(() => {
        setExpandedId(null)
        setBillingsByEntry({})
        load()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, user?.companyId])

    useEffect(() => {
        if (form) modalRef.current?.showModal()
        else modalRef.current?.close()
    }, [form])

    const shownDate = day?.date ?? dateParam
    const isToday = !!shownDate && shownDate === currentDate

    const goToDate = (value: string | null) => {
        updateParams({ date: value && value !== currentDate ? value : null })
    }

    const toggleEntry = async (entry: CashRegisterEntry) => {
        if (expandedId === entry.id) {
            setExpandedId(null)
            return
        }
        setExpandedId(entry.id)
        if (billingsByEntry[entry.id]) return

        setLoadingBillingsId(entry.id)
        try {
            const res = await getCashRegisterEntryBillings(entry.id)
            if (res.success) {
                setBillingsByEntry(prev => ({
                    ...prev,
                    [entry.id]: res.result,
                }))
            } else {
                toast.error(res.error || 'Erro ao carregar as cobranças')
            }
        } catch {
            toast.error('Erro ao carregar as cobranças')
        } finally {
            setLoadingBillingsId(null)
        }
    }

    const openCreate = () => {
        setEditing(null)
        setForm({
            direction: 'IN',
            paymentMethod: methodFilter || 'CASH',
            amount: null,
            cashDate: shownDate || currentDate || '',
            sellerId: sellerFilter,
            description: '',
        })
    }

    const openEdit = (entry: CashRegisterEntry) => {
        setEditing(entry)
        setForm({
            direction: entry.direction,
            paymentMethod: entry.paymentMethod,
            amount: entry.amount,
            cashDate: entry.cashDate,
            sellerId: entry.seller?.id ?? null,
            description: entry.description ?? '',
        })
    }

    const closeForm = () => {
        setForm(null)
        setEditing(null)
    }

    const handleSave = async () => {
        if (!form) return
        if (!form.amount || form.amount <= 0) {
            toast.error('Informe o valor do lançamento')
            return
        }
        if (!form.cashDate) {
            toast.error('Informe o dia do caixa')
            return
        }

        const payload = {
            direction: form.direction,
            paymentMethod: form.paymentMethod,
            amount: form.amount,
            cashDate: form.cashDate,
            sellerId: form.sellerId,
            description: form.description.trim(),
        }

        setSaving(true)
        try {
            const res = editing
                ? await updateCashRegisterEntry(editing.id, payload)
                : await createCashRegisterEntry({
                      ...payload,
                      // Saída sem responsável: o backend registra quem está lançando.
                      sellerId: payload.sellerId ?? undefined,
                      description: payload.description || undefined,
                  })
            if (res.success) {
                toast.success(
                    editing
                        ? 'Lançamento atualizado com sucesso'
                        : 'Lançamento registrado com sucesso'
                )
                closeForm()
                load()
            } else {
                toast.error(res.error || 'Erro ao salvar o lançamento')
            }
        } catch {
            toast.error('Erro ao salvar o lançamento')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleting) return
        setDeletingLoading(true)
        try {
            const res = await deleteCashRegisterEntry(deleting.id)
            if (res.success) {
                toast.success('Lançamento excluído com sucesso')
                setDeleting(null)
                load()
            } else {
                toast.error(res.error || 'Erro ao excluir o lançamento')
            }
        } catch {
            toast.error('Erro ao excluir o lançamento')
        } finally {
            setDeletingLoading(false)
        }
    }

    const showCompany = scope === 'organization'
    const columnCount = showCompany ? 8 : 7

    return (
        <div className="flex flex-col gap-6 animate-fade-in">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg">
                        <MdLocalAtm className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Registro de Caixa
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {closingTime && closingTime !== '00:00'
                                ? `O caixa fecha às ${closingTime}. O que entrar depois conta para o dia seguinte.`
                                : 'O caixa fecha à meia-noite.'}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={openCreate}
                    >
                        <MdAdd className="text-xl" />
                        <span className="hidden sm:inline">
                            Novo lançamento
                        </span>
                    </button>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4 flex flex-wrap items-end gap-4">
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">
                        Dia do caixa
                    </span>
                    <div className="join">
                        <button
                            type="button"
                            className="btn btn-sm join-item"
                            title="Dia anterior"
                            disabled={!shownDate}
                            onClick={() => goToDate(addDays(shownDate, -1))}
                        >
                            <MdChevronLeft className="text-lg" />
                        </button>
                        <input
                            type="date"
                            className="input input-bordered input-sm join-item w-40"
                            value={shownDate}
                            onChange={e => goToDate(e.target.value || null)}
                        />
                        <button
                            type="button"
                            className="btn btn-sm join-item"
                            title="Próximo dia"
                            disabled={!shownDate}
                            onClick={() => goToDate(addDays(shownDate, 1))}
                        >
                            <MdChevronRight className="text-lg" />
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-sm btn-ghost border border-base-300 gap-2"
                    disabled={isToday}
                    onClick={() => goToDate(null)}
                >
                    <MdToday />
                    Caixa atual
                </button>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">
                        Forma de pagamento
                    </span>
                    <select
                        className="select select-bordered select-sm w-48"
                        value={methodFilter}
                        onChange={e =>
                            updateParams({ method: e.target.value || null })
                        }
                    >
                        <option value="">Todas as formas</option>
                        {METHOD_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/70">
                        Vendedor
                    </span>
                    <UserSelector
                        value={sellerFilter}
                        onChange={id =>
                            updateParams({ seller: id ? String(id) : null })
                        }
                        label=""
                        placeholder="Todos os vendedores"
                        scope={scope}
                        className="!w-56"
                        size="sm"
                    />
                </div>

                {accessibleCompanies.length > 1 && (
                    <div className="flex items-center gap-2 sm:ml-auto">
                        <button
                            type="button"
                            className={`btn btn-sm gap-2 ${scope === 'company' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() => updateParams({ scope: null })}
                        >
                            <MdBusiness />
                            Esta unidade
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm gap-2 ${scope === 'organization' ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                            onClick={() =>
                                updateParams({ scope: 'organization' })
                            }
                        >
                            <MdHub />
                            Toda a organização
                        </button>
                    </div>
                )}
            </div>

            {shownDate && (
                <p className="text-sm text-base-content/70 -mt-2 first-letter:uppercase">
                    {formatCashDate(shownDate)}
                    {isToday && (
                        <span className="badge badge-success badge-sm text-white ml-2 align-middle">
                            Caixa aberto
                        </span>
                    )}
                </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                        <MdArrowDownward className="text-success" />
                        Entradas
                    </div>
                    <div className="text-2xl font-bold text-success mt-1">
                        {formatCurrency(day?.totals.in ?? 0)}
                    </div>
                </div>
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                        <MdArrowUpward className="text-error" />
                        Saídas
                    </div>
                    <div className="text-2xl font-bold text-error mt-1">
                        {formatCurrency(day?.totals.out ?? 0)}
                    </div>
                </div>
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                    <div className="flex items-center gap-2 text-sm text-base-content/70">
                        <MdLocalAtm className="text-secondary" />
                        Saldo do dia
                    </div>
                    <div
                        className={`text-2xl font-bold mt-1 ${(day?.totals.balance ?? 0) < 0 ? 'text-error' : 'text-base-content'}`}
                    >
                        {formatCurrency(day?.totals.balance ?? 0)}
                    </div>
                </div>
            </div>

            {day && day.methods.length > 0 && (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-x-auto">
                    <div className="px-4 pt-4 pb-2 font-semibold text-base-content">
                        Por forma de pagamento
                    </div>
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th>Forma</th>
                                <th className="text-right">Entradas</th>
                                <th className="text-right">Saídas</th>
                                <th className="text-right">Saldo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {day.methods.map(method => (
                                <tr key={method.paymentMethod}>
                                    <td className="font-medium">
                                        {PAYMENT_METHOD_LABELS[
                                            method.paymentMethod
                                        ] ?? method.paymentMethod}
                                    </td>
                                    <td className="text-right text-success">
                                        {formatCurrency(method.in)}
                                    </td>
                                    <td className="text-right text-error">
                                        {method.out
                                            ? formatCurrency(method.out)
                                            : '-'}
                                    </td>
                                    <td className="text-right font-semibold">
                                        {formatCurrency(method.balance)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-x-auto">
                <div className="px-4 pt-4 pb-2 font-semibold text-base-content">
                    Registros do dia
                </div>
                {loading && !day ? (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : !day || day.entries.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-12 text-base-content/60">
                        <MdLocalAtm className="text-4xl" />
                        <p className="text-sm">
                            Nenhum registro neste dia
                            {methodFilter ? ' para esta forma de pagamento' : ''}.
                        </p>
                    </div>
                ) : (
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                {showCompany && <th>Unidade</th>}
                                <th>Forma</th>
                                <th>Vendedor</th>
                                <th>Tipo</th>
                                <th>Origem</th>
                                <th>Descrição</th>
                                <th className="text-right">Valor</th>
                                <th className="w-24" />
                            </tr>
                        </thead>
                        <tbody>
                            {day.entries.map(entry => {
                                const isAuto = entry.source === 'AUTO'
                                const isExpanded = expandedId === entry.id
                                const billings = billingsByEntry[entry.id]
                                return (
                                    <Fragment key={entry.id}>
                                        <tr
                                            className={
                                                isAuto
                                                    ? 'cursor-pointer hover'
                                                    : undefined
                                            }
                                            onClick={
                                                isAuto
                                                    ? () => toggleEntry(entry)
                                                    : undefined
                                            }
                                        >
                                            {showCompany && (
                                                <td className="text-sm">
                                                    {entry.companyName ?? '-'}
                                                </td>
                                            )}
                                            <td className="font-medium">
                                                {PAYMENT_METHOD_LABELS[
                                                    entry.paymentMethod
                                                ] ?? entry.paymentMethod}
                                            </td>
                                            <td className="text-sm">
                                                {entry.seller?.name ?? (
                                                    <span className="text-base-content/50">
                                                        Sem vendedor
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {entry.direction === 'IN' ? (
                                                    <span className="badge badge-success badge-sm text-white">
                                                        Entrada
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-error badge-sm text-white">
                                                        Saída
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-sm">
                                                {isAuto ? (
                                                    <span className="flex items-center gap-1">
                                                        {isExpanded ? (
                                                            <MdExpandLess />
                                                        ) : (
                                                            <MdExpandMore />
                                                        )}
                                                        Automático ·{' '}
                                                        {entry.paymentCount}{' '}
                                                        {entry.direction ===
                                                        'IN'
                                                            ? entry.paymentCount ===
                                                              1
                                                                ? 'cobrança'
                                                                : 'cobranças'
                                                            : entry.paymentCount ===
                                                                1
                                                              ? 'estorno'
                                                              : 'estornos'}
                                                    </span>
                                                ) : (
                                                    <span>
                                                        Manual
                                                        {entry.createdBy && (
                                                            <span className="text-base-content/60">
                                                                {' '}
                                                                ·{' '}
                                                                {
                                                                    entry
                                                                        .createdBy
                                                                        .name
                                                                }
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-sm text-base-content/70 max-w-xs truncate">
                                                {entry.description || '-'}
                                            </td>
                                            <td
                                                className={`text-right font-semibold ${entry.direction === 'IN' ? 'text-success' : 'text-error'}`}
                                            >
                                                {entry.direction === 'OUT'
                                                    ? '- '
                                                    : ''}
                                                {formatCurrency(entry.amount)}
                                            </td>
                                            <td
                                                onClick={e =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                {!isAuto && (
                                                    <div className="flex justify-end gap-1">
                                                        {canUpdate && (
                                                            <button
                                                                className="btn btn-ghost btn-xs"
                                                                title="Editar"
                                                                onClick={() =>
                                                                    openEdit(
                                                                        entry
                                                                    )
                                                                }
                                                            >
                                                                <MdEdit className="text-base" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                className="btn btn-ghost btn-xs text-error"
                                                                title="Excluir"
                                                                onClick={() =>
                                                                    setDeleting(
                                                                        entry
                                                                    )
                                                                }
                                                            >
                                                                <MdDelete className="text-base" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                        {isAuto && isExpanded && (
                                            <tr>
                                                <td
                                                    colSpan={columnCount}
                                                    className="bg-base-200/50"
                                                >
                                                    {loadingBillingsId ===
                                                    entry.id ? (
                                                        <div className="flex justify-center py-4">
                                                            <span className="loading loading-spinner loading-sm" />
                                                        </div>
                                                    ) : !billings ||
                                                      billings.length === 0 ? (
                                                        <p className="text-sm text-base-content/60 py-2">
                                                            Nenhuma cobrança
                                                            vinculada.
                                                        </p>
                                                    ) : (
                                                        <table className="table table-xs">
                                                            <thead>
                                                                <tr>
                                                                    <th>
                                                                        Cobrança
                                                                    </th>
                                                                    <th>
                                                                        Cliente
                                                                    </th>
                                                                    <th>
                                                                        Pago em
                                                                    </th>
                                                                    <th className="text-right">
                                                                        {entry.direction ===
                                                                        'IN'
                                                                            ? 'Valor pago'
                                                                            : 'Estornado'}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {billings.map(
                                                                    billing => (
                                                                        <tr
                                                                            key={
                                                                                billing.id
                                                                            }
                                                                        >
                                                                            <td>
                                                                                <Link
                                                                                    to={`/financial/billings/${billing.id}`}
                                                                                    className="link link-hover inline-flex items-center gap-1"
                                                                                >
                                                                                    #
                                                                                    {
                                                                                        billing.id
                                                                                    }
                                                                                    <MdOpenInNew className="text-xs" />
                                                                                </Link>
                                                                                {billing.description && (
                                                                                    <span className="text-base-content/60 ml-2">
                                                                                        {
                                                                                            billing.description
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td>
                                                                                {billing
                                                                                    .client
                                                                                    ?.name ??
                                                                                    '-'}
                                                                            </td>
                                                                            <td>
                                                                                {formatTime(
                                                                                    billing.paidAt
                                                                                )}
                                                                            </td>
                                                                            <td className="text-right">
                                                                                {formatCurrency(
                                                                                    entry.direction ===
                                                                                        'IN'
                                                                                        ? (billing.paidAmount ??
                                                                                              billing.amount)
                                                                                        : (billing.refundedAmount ??
                                                                                              0)
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <dialog ref={modalRef} className="modal" onClose={closeForm}>
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-1">
                        {editing ? 'Editar lançamento' : 'Novo lançamento'}
                    </h3>
                    <p className="text-sm text-base-content/60 mb-5">
                        Lançamento manual no caixa: suprimento, sangria ou
                        recebimento registrado fora do sistema.
                    </p>

                    {form && (
                        <div className="flex flex-col gap-4">
                            <div className="form-control w-full">
                                <span className="label-text font-medium mb-2">
                                    Tipo
                                </span>
                                <div className="join w-full">
                                    <button
                                        type="button"
                                        className={`btn join-item flex-1 gap-2 ${form.direction === 'IN' ? 'btn-success text-white' : 'btn-ghost border border-base-300'}`}
                                        onClick={() =>
                                            setForm({
                                                ...form,
                                                direction: 'IN',
                                            })
                                        }
                                    >
                                        <MdArrowDownward />
                                        Entrada
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn join-item flex-1 gap-2 ${form.direction === 'OUT' ? 'btn-error text-white' : 'btn-ghost border border-base-300'}`}
                                        onClick={() =>
                                            setForm({
                                                ...form,
                                                direction: 'OUT',
                                            })
                                        }
                                    >
                                        <MdArrowUpward />
                                        Saída
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1"
                                        htmlFor="cash-entry-method"
                                    >
                                        <span className="label-text font-medium">
                                            Forma de pagamento
                                        </span>
                                    </label>
                                    <select
                                        id="cash-entry-method"
                                        className="select select-bordered w-full"
                                        value={form.paymentMethod}
                                        onChange={e =>
                                            setForm({
                                                ...form,
                                                paymentMethod: e.target.value,
                                            })
                                        }
                                    >
                                        {METHOD_OPTIONS.map(option => (
                                            <option
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-control w-full">
                                    <label
                                        className="label pb-1"
                                        htmlFor="cash-entry-amount"
                                    >
                                        <span className="label-text font-medium">
                                            Valor
                                        </span>
                                    </label>
                                    <CurrencyInput
                                        valueCents={form.amount}
                                        onChange={amount =>
                                            setForm({ ...form, amount })
                                        }
                                        className="input input-bordered w-full font-semibold"
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <span className="label-text font-medium mb-1">
                                    {form.direction === 'IN'
                                        ? 'Vendedor'
                                        : 'Responsável pela retirada'}{' '}
                                    <span className="text-base-content/50 font-normal">
                                        (opcional)
                                    </span>
                                </span>
                                <UserSelector
                                    value={form.sellerId}
                                    onChange={id =>
                                        setForm({ ...form, sellerId: id })
                                    }
                                    label=""
                                    placeholder={
                                        form.direction === 'IN'
                                            ? 'Selecione o vendedor'
                                            : 'Você, se não escolher'
                                    }
                                    className="w-full"
                                />
                                {form.direction === 'OUT' &&
                                    !form.sellerId &&
                                    !editing && (
                                        <span className="text-xs text-base-content/60 mt-2">
                                            Sem escolher, a retirada fica no seu
                                            nome.
                                        </span>
                                    )}
                            </div>

                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="cash-entry-date"
                                >
                                    <span className="label-text font-medium">
                                        Dia do caixa
                                    </span>
                                </label>
                                <input
                                    id="cash-entry-date"
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={form.cashDate}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            cashDate: e.target.value,
                                        })
                                    }
                                />
                            </div>

                            <div className="form-control w-full">
                                <label
                                    className="label pb-1"
                                    htmlFor="cash-entry-description"
                                >
                                    <span className="label-text font-medium">
                                        Descrição{' '}
                                        <span className="text-base-content/50 font-normal">
                                            (opcional)
                                        </span>
                                    </span>
                                </label>
                                <textarea
                                    id="cash-entry-description"
                                    className="textarea textarea-bordered w-full"
                                    rows={3}
                                    maxLength={500}
                                    placeholder="Ex.: troco inicial, sangria para depósito"
                                    value={form.description}
                                    onChange={e =>
                                        setForm({
                                            ...form,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    )}

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={closeForm}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary gap-2"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? (
                                <span className="loading loading-spinner loading-sm" />
                            ) : (
                                <MdSave className="text-lg" />
                            )}
                            Salvar
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button type="submit">fechar</button>
                </form>
            </dialog>

            <ConfirmModal
                isOpen={!!deleting}
                onClose={() => setDeleting(null)}
                onConfirm={handleDelete}
                loading={deletingLoading}
                title="Excluir lançamento"
                message={
                    deleting
                        ? `Excluir o lançamento manual de ${formatCurrency(deleting.amount)}? O saldo do dia será recalculado.`
                        : ''
                }
                confirmButtonText="Excluir"
            />
        </div>
    )
}
